import { ButtonGradientPrimary, Colors, Container, Text } from '@amelio/ui-library';
import { useEffect } from 'react';
import { FormProvider } from 'react-hook-form';

import type { ThematicMeeting } from '@/types/thematic-meetings';

import { useGetCareerAspiration, useGetCareerAspirationHistory } from '@/api/performance-profile';
import { BlankAspirationConfirmDialog } from '@/components/CareerAspiration/BlankAspirationConfirmDialog';
import { CareerAspirationAnswers } from '@/components/CareerAspiration/CareerAspirationAnswers';
import { CareerAspirationForm } from '@/components/CareerAspiration/CareerAspirationForm';
import { toFormValues } from '@/components/CareerAspiration/careerAspirationValidation';
import { useCareerAspirationForm } from '@/components/CareerAspiration/useCareerAspirationForm';
import { PlanCard } from '@/components/MeetingPlan/PlanSection';
import { Bone, Skeleton } from '@/components/Skeleton/Skeleton';
import { useAmelioTranslations } from '@/hooks';
import { CareerAspirationBanner } from '@/pages/ThematicMeetings/Plan/components/CareerAspirationBanner';
import { usePendingSave } from '@/pages/ThematicMeetings/Plan/components/PendingSavesScope';

interface CareerAspirationSectionProps {
    meeting: ThematicMeeting;
    disabled?: boolean;
    dataTestId?: string;
}

/**
 * The career meeting's form: the "Aspirations de carrière" questions the performance profile's
 * "Croissance" tab asks and shows, recorded on this meeting's own aspiration. Saved in one request
 * from its button, the way the API replaces the whole picture; once the meeting is completed, the
 * answers are read instead.
 */
export const CareerAspirationSection = ({
    meeting,
    disabled = false,
    dataTestId = 'thematic-meeting-career-aspiration',
}: CareerAspirationSectionProps) => {
    const { t } = useAmelioTranslations();
    const employeeFirstName = meeting.employee?.firstName ?? '';

    const history = useGetCareerAspirationHistory(meeting.employeeId);
    const aspirationId =
        history.data?.find((entry) => entry.thematicMeetingId === meeting.id)?.aspirationId ??
        undefined;
    const aspirationQuery = useGetCareerAspiration(meeting.employeeId, aspirationId);

    // A disabled query stays pending forever, so the answers only count as loading when there are
    // answers to load. Until both reads settle, the section concludes nothing: an empty form would
    // invite a second aspiration on this meeting, and an empty sentence would claim nothing was said.
    const isLoading = history.isPending || (!!aspirationId && aspirationQuery.isPending);
    const hasFailed = history.isError || (!!aspirationId && aspirationQuery.isError);
    const aspiration = aspirationId ? (aspirationQuery.data ?? null) : null;

    const { form, save, isSaving, blankSaveConfirm } = useCareerAspirationForm(
        meeting.employeeId,
        aspiration,
        meeting.id,
    );
    const {
        reset,
        handleSubmit,
        formState: { isDirty },
    } = form;

    const saveAnswers = async () => {
        let isSaved = false;
        await handleSubmit(async (values) => {
            isSaved = await save(values);
            // Clean at once: a first save only finds its aspiration once the history is read again,
            // and a second one meanwhile would record another.
            if (isSaved) {
                reset(values);
            }
        })();
        return isSaved;
    };

    usePendingSave(!disabled && isDirty ? saveAnswers : null);

    // Seeds the form once the answers are known, and again when a save brings them back. A refetch
    // that returns the same answers keeps the same object, so it never clobbers what is being typed.
    useEffect(() => {
        if (!isLoading && !hasFailed) {
            reset(toFormValues(aspiration));
        }
    }, [isLoading, hasFailed, aspiration, reset]);

    const renderContent = () => {
        if (isLoading) {
            return (
                <div
                    role={'status'}
                    aria-busy={'true'}
                    data-testid={`${dataTestId}-loading`}
                >
                    <Skeleton>
                        <Bone className={'h-40 w-full'} />
                    </Skeleton>
                </div>
            );
        }

        if (hasFailed) {
            return (
                <div role={'alert'}>
                    <Text color={Colors.ERROR_800}>
                        {t('thematicMeetings.plan.careerAspirationLoadFailed')}
                    </Text>
                </div>
            );
        }

        if (disabled) {
            return aspiration ? (
                <div className={'flex flex-col gap-5'}>
                    <CareerAspirationAnswers
                        aspiration={aspiration}
                        employeeFirstName={employeeFirstName}
                        dataTestId={`${dataTestId}-answers`}
                    />
                </div>
            ) : (
                <Text color={Colors.BASE_500}>
                    {t('thematicMeetings.plan.careerAspirationEmptyCompleted')}
                </Text>
            );
        }

        const saveLabel = t('performanceProfile.growth.aspiration.form.save');

        return (
            <FormProvider {...form}>
                <CareerAspirationForm
                    employeeFirstName={employeeFirstName}
                    dataTestId={`${dataTestId}-form`}
                />

                <div className={'mt-6 flex flex-wrap items-center justify-between gap-3'}>
                    <Text
                        size={'small'}
                        color={Colors.BASE_500}
                    >
                        {t('performanceProfile.growth.aspiration.form.footerHint')}
                    </Text>

                    {/* A button, not a form submission: `Entrée` in one of the fields must never
                        save the answers of a plan whose other sections save on their own. */}
                    <ButtonGradientPrimary
                        size={'sm'}
                        fitContent
                        onClick={() => void saveAnswers()}
                        loading={isSaving}
                        disabled={isSaving}
                        // The library swaps the label for a spinner while saving; the name holds.
                        ariaLabel={saveLabel}
                        dataTestId={`${dataTestId}-save`}
                    >
                        {saveLabel}
                    </ButtonGradientPrimary>
                </div>

                <BlankAspirationConfirmDialog {...blankSaveConfirm} />
            </FormProvider>
        );
    };

    return (
        <PlanCard
            padding={'none'}
            className={'overflow-hidden'}
            dataTestId={dataTestId}
        >
            <CareerAspirationBanner employeeFirstName={employeeFirstName} />

            <Container p={'xl'}>{renderContent()}</Container>
        </PlanCard>
    );
};
